import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

// mui
import { styled } from "@mui/material/styles";
import { Typography, Box, Drawer } from "@mui/material";

// hooks
import useResponsive from "../../hooks/useResponsive";

// components
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
import Logo from "../../components/asset/Logo";

import sidebarConfig from "./SidebarConfig";
import { useCan } from "@auth";
import { DRAWER_WIDTH } from "./define";


const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const SideHeader = () => (
  <Box sx={{ px: 2.5, py: 2.5 }}>
    <Box
      component={RouterLink}
      to="/"
      style={{ textDecoration: "none" }}
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Logo />
      <Typography variant="h4" sx={{ ml: 2, color: "black" }}>
        BTS
      </Typography>
    </Box>
  </Box>
)

const SideMenu = () => {
  const can = useCan();

  // 권한이 걸린 메뉴는 그 권한이 있을 때만 보인다. 항목이 다 빠진 섹션은 헤더까지 감춘다.
  // 화면에서 숨기는 것은 보안이 아니다 — 서버가 같은 권한으로 데이터를 막는다.
  const navConfig = useMemo(
    () =>
      sidebarConfig
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.permission || can(item.permission)),
        }))
        .filter((section) => section.items.length > 0),
    [can],
  );

  return (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <SideHeader/>
      <NavSection navConfig={navConfig} />
    </Scrollbar>
  )
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const isDesktop = useResponsive("up", "lg");

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <RootStyle>
      { (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          <SideMenu/>
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
              borderRightStyle: "solid",
            },
          }}
        >
          <SideMenu/>
        </Drawer>
      )}
    </RootStyle>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

